<template>
    <b-modal ref="datavantTokenizationContainer" :hide-header="true" :hide-footer="true" scrollable
        :no-close-on-backdrop="true" :no-close-on-esc="true" size="lg" :body-class="[]">
        <b-container fluid class="datavant-token-container">
            <b-row class="header">
                <b-col cols="12">
                    <span class="title">Set Up Datavant Configurations & Tokens</span>
                    <span class="cross-icon" @click="closeModal()">&times;</span>
                </b-col>
            </b-row>
            <b-row>
                <b-col cols="12">
                    <p class="sub-heading">
                        Please input the Configuration Name and Data Set Name from the
                        Datavant website.
                    </p>
                </b-col>
            </b-row>
            <div class="form-group">
                <label class="label-Name">Configuration Name<img src="static/img/Asterisk - Required.svg" /></label>
                <input type="text" class="input-text"
                    placeholder="Please enter the Configuration Name as specified in Datavant Portal."
                    v-model="configFileName" :disabled="!isDatavantTokenEditable" />
                <label class="label-Name">Data Set Name<img src="static/img/Asterisk - Required.svg" /></label>
                <input type="text" class="input-text"
                    placeholder="Please enter the Dataset Name as specified in Datavant Portal." v-model="setName" :disabled="!isDatavantTokenEditable" />
            </div>
            <b-col cols="12">
                <p class="sub-heading">
                    Please select the tokens you wish to include. The first 2 Core Tokens cannot be unselected.
                </p>
            </b-col>
            <b-row class="token-list-section">
                <b-col cols="6">
                    <div class="token-title">Core Tokens</div>
                    <div class="token-field disabled-label" v-for="(token, index) in coreTokens" :key="`${token && token.id}-${index}-id`">
                        <label class="checkbox-label">
                            <input :id="`${token.id}-token`" :value="token.id" type="checkbox" v-model="token.enabled"
                               disabled/>
                            <span class="input-checkbox"></span>
                        </label>
                        <span class="token-number">{{ token.sequence }}</span>
                        <span class="hyphen">–</span>
                        <span class="token-text">{{ token.tokenValue }}</span>
                    </div>
                </b-col>
                <b-col cols="6">
                    <div class="token-title">Standard Tokens</div>
                    <div class="token-field" v-for="(token, index) in standardTokens"
                        :key="`${token && token.id}-${index}-id`" :class="{'disabled-label': !isDatavantTokenEditable}">
                        <label class="checkbox-label">
                        <input :id="`${token.id}-token`" :value="token.id" type="checkbox" v-model="token.enabled" :disabled="!isDatavantTokenEditable" />
                        <span class="input-checkbox"></span>
                        </label>
                        <span class="token-number">{{ token.sequence }}</span>
                        <span class="hyphen">–</span>
                        <span class="token-text">{{ token.tokenValue }}</span>
                    </div>
                </b-col>
            </b-row>
            <b-row class="btn-section">
                <button class="token-btn" @click="applyTokens()" :disabled="!isDatavantTokenEditable">Apply Tokens</button>
                <button class="cancel-btn" @click="closeModal()">Cancel</button>
            </b-row>
        </b-container>
    </b-modal>
</template>
<script>
import moment from "moment";
import { clone, cloneDeep } from "lodash";

export default {
    name: "datavant-tokenization-modal",
    props: {
        datavantTokenDetails: {
            required: true,
            type: Array,
            default: () => []
        },
        configurationFileName: {
            required: true,
            type: String,
        },
        datasetName : {
            required: true,
            type: String,
        },
        isDatavantTokenEditable : {
            required: true,
            type: Boolean
        }    
    },
    watch: {
        datavantTokenDetails: {
            immediate: true,
            deep : true,
            handler(newVal) {
                if (newVal) {
                    this.updateTokenList();                  
                }
            }
        },
        configurationFileName(configFileName) {
            this.configFileName = clone(configFileName);
        },
        datasetName(setName) {
            this.setName = clone(setName);
        }
    },
    data() {
        return {
            tokenList: [],
            configFileName: "",
            setName: "",
            coreTokens: [],
            standardTokens: [],           
        };
    },
    methods: {
        openModal() {
            this.$refs.datavantTokenizationContainer.show();
        },
        closeModal() {
            this.configFileName = clone(this.configurationFileName);
            this.setName = clone(this.datasetName);
            this.updateTokenList();
            this.$emit("on-datavant-modal-close");
            this.$refs.datavantTokenizationContainer.hide();
        },
        updateTokenList() {
            this.tokenList = cloneDeep(this.datavantTokenDetails);
            this.seperateCoreAndStandardTokens();
        },
        seperateCoreAndStandardTokens() {
            this.coreTokens = this.tokenList
                .filter(token => {
                    if (token.tokenType === "Core") {
                        token.enabled = true;
                        return true;
                    }
                    return false;
                }).sort((a, b) => a.sequence - b.sequence);

            this.standardTokens = this.tokenList
                .filter(token => {
                    return token.tokenType === "Standard";
                }).sort((a, b) => a.sequence - b.sequence);
        },
        applyTokens() {
            if (!this.configFileName || this.configFileName.trim() === '' || !this.setName || this.setName.trim() === '') {
                this.$toastr.error("Please fill out all fields.");
                return;
            }
            if (this.configFileName.length > 500) {
                this.$toastr.error("Configuration name should be 500 characters or less.");
                return;
            }
            const allowedSpecialCharacters = /^[A-Za-z0-9_]+$/;
            if (!allowedSpecialCharacters.test(this.configFileName)) {
                this.$toastr.error("Configuration name should only contain numbers, letters and underscores.");
                return;
            }
            if (this.setName.length > 255) {
                this.$toastr.error("Data Set name should be 255 characters or less.");
                return;
            }
            let pathParams = {
                studyId: this.$route.params.studyId
            };
            this.tokenList = this.coreTokens.concat(this.standardTokens);
            let payloadData = {
                tokensModified: true,
                dataVantTokens: this.tokenList.map(token => {
                    token.modifiedAt = moment().valueOf();
                    return token;
                })
            };
            this.$api.saveDatavantTokenList(pathParams, payloadData).then(data => {
                this.$emit("selected-token-list", {
                    tokenListData: payloadData.dataVantTokens,
                    isModalOpen: true
                });
            });
            let requestPayload = {
                studyId: this.$route.params.studyId,
                configurationFileName: this.configFileName,
                datasetName: this.setName
            };
            this.$api
                .saveConfigurationAndDataSetName(pathParams, requestPayload)
                .then(data => {
                    this.$emit("config-name", {
                        configurationFileName: this.configFileName,
                        datasetName: this.setName
                    });
                    this.$refs.datavantTokenizationContainer.hide();
                    this.$toastr.success("Tokens added.");
                },
                    error => {
                        this.$toastr.error(
                            error.data && error.data.message
                                ? error.data.message
                                : "Error while updating Configuration Name and Data Set Name."
                        );
                    }
                );
        }
    }
};
</script>
<style lang="scss" scoped>
/deep/ .modal-dialog {
    width: 100% !important;
}

/deep/ .modal-body {
    padding: 25px 0 !important;
}

@media (min-width: 992px) {
    /deep/ .modal-lg {
        max-width: 1122px !important;
    }
}

.datavant-token-container {
    padding-right: 0 !important;
    padding-left: 0 !important;

    .header {
        line-height: 1rem;

        .title {
            font-family: "RobotoRegular";
            font-size: 22px;
            font-weight: 500;
            color: #4a4a4a;
            padding-left: 13px;
            font-family: Roboto-Medium;
        }

        .cross-icon {
            float: right;
            font-size: 31px;
            color: #3c3c3d;
            font-weight: 500;
            cursor: pointer;
        }
    }

    .sub-heading {
        margin: 7px 0px 20px 14px;
        font-size: 18px;
        color: #4a4a4a;
        font-family: Roboto-Medium;
        margin-top: 26px;
    }

    .form-group {
        margin-top: -9px;
    }

    .label-Name {
        font-size: 14px;
        font-family: Roboto-Regular;
        padding-left: 29px;
        margin-top: 13px;
    }

    img {
        margin: 4.1px 0 4.1px 4px;
        margin-bottom: 5px;
    }

    .input-text {
        width: 95%;
        border-radius: 4px !important;
        border: solid 1px #d3dfe4;
        height: 36px;
        background-color: #fff;
        text-indent: 10px;
        margin-left: 29px;
        margin-top: -6px;
        &:disabled{
            opacity: 0.5;
        }
    }

    .token-title {
        font-size: 14px;
        font-family: Roboto-Medium;
        color: #4a4a4a;
        margin-bottom: 15px;
    }

    .token-field {
        margin-bottom: 15px;
        display: flex;
        align-items: center;
    }

    .token-list-section {
        padding-left: 12px;

        input[type="checkbox"] {
            cursor: pointer;
            height: 20px;
            width: 20px;
            border-radius: 4px;
            border: solid 1px #959fa4;
        }

        .token-text {
            color: #666669;
            font-size: 14px;
            margin-left: 8px;
        }

        .token-number {
            font-size: 13px;
            margin-left: 10px;
        }

        .checkbox-label {
            position: relative;
            margin-bottom: 8px;
            cursor: pointer;
            margin-right: 22px;
          
            input {
                opacity: 0;
                position: absolute;
                cursor: pointer;
                z-index: 999;
                width: 18px;
                height: 19px;
                margin-left: -9px;

                &:checked ~ .input-checkbox {
                    background-color: #1e8fe1;
                    border-radius: 4px;
                    transform: rotate(0deg) scale(1);
                    border: 1.5px solid #1e8fe1;

                    &::after {
                        transform: rotate(45deg) scale(1);
                        opacity: 1;
                        left: 6px;
                        top: 1px;
                        width: 6px;
                        height: 12px;
                        border: solid #ffffff;
                        border-width: 0px 2px 2px 0px;
                        background-color: transparent;
                        border-radius: 0;
                    }
                }
                &:disabled ~ .input-checkbox {
                    opacity: 0.5;
                }
            }
        }
        .input-checkbox {
            position: absolute;
            top: -5px;
            height: 20px;
            width: 20px;
            background-color: #fff;
            border-radius: 4px;
            border: 1.5px solid #959fa4;
          
            &::after {
                position: absolute;
                content: "";
            }
        }         
        .disabled-label {
            opacity: 0.5;
        }
        .hyphen {
            margin-left: 6px;
            color: #959fa4;
        }
    }

    .core-token-style {
        float: left;
    }

    .standard-token-style {
        float: right;
    }

    .btn-section {
        -webkit-box-orient: vertical;
        -webkit-box-direction: normal;
        flex-flow: row-reverse wrap;
        -ms-flex-line-pack: end;
        align-content: flex-end;
        margin-top: 10px;

        & button {
            padding: 6px 14px;
            display: inline;
            cursor: pointer;

            &.cancel-btn {
                padding: 6px 40px;
                border: 1px solid #4c8ce4;
                color: #4c8ce4;
                background-color: #fff;
                padding: 1px 24px;
                border-radius: 4px;
                border: solid 1px #4c8ce4;
                width: 146.9px;
                height: 41px;
            }

            &.token-btn {
                padding: 6px 34px;
                margin: 0px 7px;
                color: #fff;
                border: 1px solid #4c8ce4;
                background-color: #4c8ce4;
                padding: 1px 24px;
                border-radius: 4px;
                width: 146.9px;
                height: 41px;
                margin-left: 10px;
                margin-right: 25px;
                &:disabled{
                    opacity: 0.5;
                }
            }
        }
    }
}</style>